import { useState, useEffect } from 'react';
import Modal from './Modal';
import { User, Building, Mail, Phone, MapPin, Save } from 'lucide-react';
import CitySelectorForm from '../../inputs/CitySelectorForm';
import { getAuthHeaders } from '@/utils/getauthheaders';

const AddClientModal = ({ isOpen, onClose, client = null, onSave }) => {
  // State pentru formularul de client
  const [formData, setFormData] = useState({
    nume_firma: '',
    email: '',
    telefon: '',
    address: '',
    oras: '', 
    judet: '',
    cui: '',
    notes: ''
  });

  // Preîncarcă datele clientului dacă e în modul editare
  useEffect(() => {
    if (isOpen) { // Reset when modal opens
      setFormData({
        nume_firma: client?.nume_firma || '',
        email: client?.email || '',
        telefon: client?.telefon || '',
        // Fixed: use 'adresa' from backend, map to 'address' in frontend
        address: client?.adresa || '',
        // Fixed: use 'oras' consistently
        oras: client?.oras || '',
        judet: client?.judet || '',
        cui: client?.cui || '',
        notes: client?.notes || ''
      });
    }
  }, [client, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    let locationId = null;

    // Creează locația dacă există date de adresă
    if (formData.address) {
      const locationData = {
        adresa: formData.address,
        oras: formData.oras,
        judet: formData.judet || null,
        tara: 'Romania',
        tip: 'plecare'
      };

      const locationResponse = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/locations`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
          },
          body: JSON.stringify(locationData),
        }
      );

      const locationResult = await locationResponse.json();
      if (locationResult.success) {
        locationId = locationResult.location.id;
      } else {
        console.error('Eroare la creare locație:', locationResult.error);
        alert(`Eroare la creare locație: ${locationResult.error}`);
        return;
      }
    }

    // Creează clientul
    const clientData = {
      nume_firma: formData.nume_firma,
      email: formData.email,
      telefon: formData.telefon,
      cui: formData.cui,
      notes: formData.notes,
      location_id: locationId
    };

    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/api/clients`,
      {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
        },
        body: JSON.stringify(clientData),
      }
    );

    const data = await response.json();

    if (data.success) {
      console.log('Client creat cu succes');
      onSave?.(data.client || data);
      onClose();
      setFormData({
        nume_firma: '',
        email: '',
        telefon: '',
        address: '',
        oras: '',
        judet: '',
        cui: '',
        notes: ''
      });
    } else {
      console.error('Eroare:', data.error);
      alert(`Eroare la creare client: ${data.error}`);
    }

  } catch (error) {
    console.error('Eroare la trimitere:', error);
    alert('Eroare la comunicarea cu serverul');
  }
};

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={client ? 'Editează Client' : 'Client Nou'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nume și CUI */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Nume Firmă *
            </label>
            <input
              type="text"
              value={formData.nume_firma}
              onChange={e => handleChange('nume_firma', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4 inline mr-1" />
              Cod Fiscal (CUI)
            </label>
            <input
              type="text"
              value={formData.cui}
              onChange={e => handleChange('cui', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Email și Telefon */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-1" />
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={e => handleChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 inline mr-1" />
              Telefon *
            </label>
            <input
              type="tel"
              value={formData.telefon}
              onChange={e => handleChange('telefon', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        {/* Adresă și Oraș */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Adresă (Plecare)
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={e => handleChange('address', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: Str. Republicii nr. 12"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Oraș
            </label>
            <CitySelectorForm
              selectedCity={formData.oras} // Fixed: pass the actual city value
              onCityChange={(city) => {
                if (typeof city === 'string') {
                  // Handle string input (for manual typing)
                  setFormData(prev => ({
                    ...prev,
                    oras: city,
                    judet: '', // Clear county if manually typed
                  }));
                } else {
                  // Handle city object from selector
                  setFormData(prev => ({
                    ...prev,
                    oras: city.name,
                    judet: city.county,
                    cod_judet: city.countyCode,
                  }));
                }
              }}
            />
            {/* Județ afișat dedesubt */}
            {formData.judet && (
              <div className="mt-2 px-3 py-2 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg">
                <div className="flex items-center text-sm">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                  <span className="text-gray-600 mr-1">Județ:</span>
                  <span className="text-gray-800 font-medium">{formData.judet}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notițe */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notițe
          </label>
          <textarea
            value={formData.notes}
            onChange={e => handleChange('notes', e.target.value)}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Notițe despre client..."
          />
        </div>

        {/* Butoane */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Anulează
          </button>
          <button
            type="submit"
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg flex items-center hover:from-orange-600 hover:to-red-600 transition-colors"
          >
            <Save className="w-4 h-4 mr-2" />
            {client ? 'Actualizează' : 'Salvează'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddClientModal;